'use client'

import { useQuery } from '@tanstack/react-query'
import { getCategories, type MeiliCategory } from '@/app/lib/meilisearch'
import { getCategoryDisplayName } from '@/app/lib/constants'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CategoryPostersProps {
  onCategoryClick?: (categoryName: string) => void
}

/* ------------------------------------------------------------------ */
/*  Gradient map                                                       */
/* ------------------------------------------------------------------ */

const categoryGradients: Record<string, string> = {
  'Deep Pockets':
    'linear-gradient(160deg, #050e05 0%, #0a2a0a 40%, #1a6b1a 80%, #2d9c2d 100%)',
  'The Swag Academy':
    'linear-gradient(160deg, #05050e 0%, #0a0a2a 40%, #1a1a7a 80%, #2244cc 100%)',
  'The Goat Academy':
    'linear-gradient(160deg, #080e05 0%, #162a08 40%, #2d6b0a 80%, #44a010 100%)',
  'Shift Szn':
    'linear-gradient(160deg, #0e0514 0%, #2a0a40 40%, #6600aa 80%, #9933ee 100%)',
  'YouTube Archived':
    'linear-gradient(160deg, #140505 0%, #400a0a 40%, #aa1100 80%, #ff2200 100%)',
  'Personal Finance':
    'linear-gradient(160deg, #050e0e 0%, #0a2a2a 40%, #0a7a6b 80%, #10bbaa 100%)',
  'Unseen Footage':
    'linear-gradient(160deg, #0e0e05 0%, #2a2800 40%, #6b6400 80%, #D4A017 100%)',
}

const defaultGradient =
  'linear-gradient(160deg, #1a1400 0%, #3a2e00 40%, #D4A017 100%)'

/* ------------------------------------------------------------------ */
/*  Noise texture SVG (matches the global body::after grain)           */
/* ------------------------------------------------------------------ */

const noiseSvg =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")"

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function CategoryPosters({ onCategoryClick }: CategoryPostersProps) {
  const { data: categories = [], isLoading } = useQuery<MeiliCategory[]>({
    queryKey: ['meilisearch-categories'],
    queryFn: getCategories,
    staleTime: 60 * 60 * 1000,
  })

  if (!isLoading && categories.length === 0) return null

  return (
    <section className="relative z-[2] bg-background pb-20 pt-12 md:pt-12 lg:pt-12">
      {/* Inner container */}
      <div className="px-6 md:px-10 lg:px-[72px]">
        {/* ---- Header ---- */}
        <div className="mb-7 flex items-baseline justify-between">
          <div>
            {/* Browse label */}
            <span className="mb-1 flex items-center gap-2.5 font-condensed text-[11px] font-bold uppercase tracking-[4px] text-gold">
              <span className="inline-block h-px w-7 bg-gold" />
              Browse
            </span>

            {/* Heading */}
            <h2
              className="font-heading leading-none tracking-[3px] text-white"
              style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}
            >
              ALL{' '}
              <span className="text-gold">CATEGORIES</span>
            </h2>
          </div>
        </div>

        {/* ---- Poster scroll container ---- */}
        <div className="relative">
          <div className="hide-scrollbar flex gap-3.5 overflow-x-auto py-2 pb-4">
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <PosterSkeleton key={i} />
                ))
              : categories.map((category) => (
                  <PosterCard
                    key={category.name}
                    category={category}
                    gradient={categoryGradients[category.name] ?? defaultGradient}
                    onClick={() => onCategoryClick?.(category.name)}
                  />
                ))}
          </div>
        </div>
      </div>

      {/* ---- Bottom separator ---- */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(212,160,23,0.5), transparent)',
        }}
      />
    </section>
  )
}

/* ------------------------------------------------------------------ */
/*  Poster Skeleton                                                    */
/* ------------------------------------------------------------------ */

function PosterSkeleton() {
  return (
    <div
      className="w-[130px] flex-shrink-0 overflow-hidden rounded-sm border border-border [aspect-ratio:2/3] md:w-[160px]"
    >
      <div className="h-full w-full animate-pulse bg-muted" />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Poster Card                                                        */
/* ------------------------------------------------------------------ */

interface PosterCardProps {
  category: MeiliCategory
  gradient: string
  onClick: () => void
}

function PosterCard({ category, gradient, onClick }: PosterCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Browse ${getCategoryDisplayName(category.name)} — ${category.count} videos`}
      className="
        group
        relative
        w-[130px]
        flex-shrink-0
        cursor-pointer
        overflow-hidden
        rounded-sm
        border
        border-border
        transition-all
        duration-[250ms]
        [aspect-ratio:2/3]
        hover:-translate-y-1.5
        hover:scale-[1.04]
        hover:border-gold
        hover:shadow-[0_0_0_2px_#D4A017,0_16px_40px_rgba(0,0,0,0.7),0_0_30px_rgba(212,160,23,0.3)]
        md:w-[160px]
      "
    >
      {/* Left accent line on hover */}
      <span
        className="
          pointer-events-none
          absolute
          bottom-0
          left-0
          top-0
          z-[4]
          w-[3px]
          origin-bottom
          scale-y-0
          bg-gold
          transition-transform
          duration-[250ms]
          group-hover:scale-y-100
        "
      />

      {/* 1 · Background gradient */}
      <div className="absolute inset-0" style={{ background: gradient }} />

      {/* 2 · Grain texture */}
      <div
        className="pointer-events-none absolute inset-0 z-[1] opacity-[0.08]"
        style={{ backgroundImage: noiseSvg }}
      />

      {/* 3 · Dark overlay */}
      <div
        className="absolute inset-0 z-[2]"
        style={{
          background:
            'linear-gradient(to top, rgba(5,5,5,0.92) 0%, rgba(5,5,5,0.3) 50%, rgba(5,5,5,0.05) 100%)',
        }}
      />

      {/* 4 · Top icon badge */}
      <span
        className="
          absolute
          right-3
          top-3
          z-[3]
          flex
          h-7
          w-7
          scale-[0.7]
          items-center
          justify-center
          rounded-full
          border
          border-border-bright
          bg-gold/15
          opacity-0
          transition-all
          duration-[250ms]
          group-hover:scale-100
          group-hover:opacity-100
        "
      >
        {/* Play icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="h-3 w-3 text-gold"
          aria-hidden="true"
        >
          <path d="M8 5.14v14l11-7-11-7z" />
        </svg>
      </span>

      {/* 5 · Content */}
      <div className="absolute bottom-0 left-0 right-0 z-[3] px-3.5 pb-4 pt-3 text-left">
        {/* Count */}
        <span className="mb-1 flex items-center gap-[5px] font-condensed text-[10px] font-bold uppercase tracking-[2px] text-gold">
          <span className="inline-block h-px w-3.5 bg-gold" />
          {category.count} Videos
        </span>

        {/* Name */}
        <span
          className="block font-heading text-lg leading-none tracking-[1.5px] text-white"
        >
          {getCategoryDisplayName(category.name)}
        </span>
      </div>
    </button>
  )
}
